/**
 * persistence/hooks/useFirestoreCommitHistory.js
 *
 * Firestore-ONLY commit history, for the management portal (managers run in a
 * different browser/device with no local IndexedDB data).
 *
 * Reads the analyst's pushed VCS commits and decodes each Yjs snapshot back to
 * its event string, returning the same shape the version cards already consume
 * ({ string, eventCount, triggerType, createdAt, versionNumber, diffSummary }).
 * This replaces the legacy useFirestoreVersionHistory now that commits are the
 * single source of truth.
 *
 * @param {{ matchId: string|number, analystId: string|number }} params
 * @returns {{ versions, loading, error, refresh }}
 */

import { useState, useEffect, useCallback } from "react";
import { getFirestoreClient } from "../firestore/firestoreClient";
import {
  FIRESTORE_SESSIONS_COLLECTION,
  FIRESTORE_COMMITS_SUBCOLLECTION,
} from "../config";
import { loadDoc, docToString, decodeUpdateBase64 } from "../vcs/yjsDoc";

export function useFirestoreCommitHistory({ matchId, analystId }) {
  const [versions, setVersions] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const loadCommits = useCallback(async () => {
    if (!matchId || !analystId) {
      setVersions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setVersions([]);

    try {
      const fsClient = await getFirestoreClient();
      if (!fsClient) {
        setError("Firestore is not configured — check REACT_APP_FIREBASE_* env vars.");
        return;
      }

      const { collection, getDocs, orderBy, query } = await import("firebase/firestore");

      const docId = `${analystId}_${matchId}`;
      const commitsRef = collection(
        fsClient,
        FIRESTORE_SESSIONS_COLLECTION,
        docId,
        FIRESTORE_COMMITS_SUBCOLLECTION
      );

      const q = query(commitsRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);

      const loaded = snapshot.docs.map((doc) => {
        const c = doc.data();
        // Decode the Yjs snapshot back to the event string for display.
        let string = "";
        try {
          string = docToString(loadDoc(decodeUpdateBase64(c.stateUpdate)));
        } catch {
          string = "";
        }
        return {
          id:            c.commitId ?? doc.id,
          matchId:       String(matchId),
          analystId:     String(analystId),
          string,
          eventCount:    c.eventCount ?? 0,
          // The cards key trigger styling off triggerType; commits carry it.
          triggerType:   c.triggerType ?? "unknown",
          message:       c.message ?? "",
          createdAt:     c.createdAt?.toMillis?.() ?? 0,
          versionNumber: c.versionNumber ?? null,
          diffSummary:   c.diffSummary ?? null,
          client:        c.client ?? null,
          headConflict:  Boolean(c.headConflict),
        };
      });

      setVersions(loaded);
    } catch (err) {
      const msg = err.message ?? "Unknown error";
      setError(msg);
      console.error("[useFirestoreCommitHistory] Load failed:", msg);
    } finally {
      setLoading(false);
    }
  }, [matchId, analystId]);

  useEffect(() => {
    loadCommits();
  }, [loadCommits]);

  return { versions, loading, error, refresh: loadCommits };
}
